/**
 * The Live Event screen's theme: what the admin form edits, and how it maps to
 * the API.
 *
 * Key names are the app's own (`card_gradient_start`, `check_in_button_text`, …)
 * and are used unchanged here, in the request, in the database and in
 * `GET /v1/event/current`. Mirrors `src/common/event-theme.js` in the API.
 *
 * Every field is optional. Empty means "the spec default": the API fills it in
 * before sending the theme to the app (`resolveTheme` here is the same rule), so
 * the preview shows exactly what the app receives.
 */

/** `#RRGGBB`, or `#RRGGBBAA` with the alpha last (`#FFFFFF33` = white at 20%). */
export const HEX_COLOR = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

export const isHexColor = (value) => HEX_COLOR.test(String(value || "").trim());

export const CARD_BG_TYPES = ["color", "image"];

/**
 * Colour fields, grouped as the form shows them. `default` is the app's own
 * fallback from the spec.
 */
export const COLOR_GROUPS = [
  {
    key: "background",
    title: "Card background",
    hint: "Used in Color mode, and when an image fails to load",
    fields: [
      { field: "card_gradient_start", label: "Gradient start", default: "#636AE8", hint: "Top-left · default: solid color" },
      { field: "card_gradient_end", label: "Gradient end", default: "#636AE8B3", hint: "Bottom-right · default: start at 70%" },
      { field: "card_bg_color", label: "Solid color", default: "#636AE8", hint: "Set only this for a one-color card · default: primary" },
    ],
  },
  {
    key: "card",
    title: "Card text & badges",
    fields: [
      { field: "card_title_color", label: "Title", default: "#FFFFFF" },
      { field: "card_subtitle_color", label: "Subtitle", default: "#FFFFFFB3" },
      { field: "card_accent_color", label: "Accent", default: "#FFC107", hint: "Countdown, progress bar, day highlights" },
      { field: "card_points_color", label: "Points number", default: "#FFFFFF" },
      { field: "card_rank_badge_bg_color", label: "Rank badge background", default: "#00000033" },
      { field: "card_rank_badge_text_color", label: "Rank badge text", default: "#FFFFFF" },
      { field: "card_shadow_color", label: "Card shadow", default: "#636AE84D", hint: "Default: gradient start at 30%" },
    ],
  },
  {
    key: "check_in",
    title: "Daily check-in button",
    fields: [
      { field: "check_in_btn_bg_color", label: "Button background", default: "#FFFFFF", hint: "Before check-in" },
      { field: "check_in_btn_text_color", label: "Button text", default: "#636AE8", hint: "Before check-in · default: primary" },
      { field: "checked_in_btn_bg_color", label: "Checked-in background", default: "#FFFFFF33" },
      { field: "checked_in_btn_text_color", label: "Checked-in text", default: "#FFFFFFB3" },
    ],
  },
  {
    key: "screen",
    title: "Screen & app bar",
    fields: [
      { field: "primary_color", label: "Primary", default: "#636AE8", hint: "Buttons, icons, badges, borders" },
      { field: "screen_bg_color", label: "Screen background", default: "#FFFFFF" },
      { field: "app_bar_title_color", label: "App bar title & icons", default: "#000000" },
    ],
  },
];

export const COLOR_FIELDS = COLOR_GROUPS.flatMap((group) => group.fields);

/** Text overrides. `default` null means it falls back to something else (noted in `hint`). */
export const TEXT_FIELDS = [
  { field: "app_bar_title", label: "App bar title", default: "Live Event", max: 100 },
  { field: "card_title", label: "Card title", default: null, hint: "Defaults to the event name", max: 191 },
  {
    field: "card_subtitle",
    label: "Card subtitle",
    default: "Collect PX, stay active, and climb the leaderboard.",
    max: 500,
    multiline: true,
  },
  {
    field: "coming_soon_subtitle",
    label: "Coming soon text",
    default: "All Creators, Pioneers, and Miners! 🔥\nAn epic new challenge is approaching...",
    hint: "Shown instead of the subtitle before the event opens",
    max: 1000,
    multiline: true,
  },
  { field: "points_label", label: "Points label", default: "Event Points", max: 100 },
  { field: "check_in_title", label: "Check-in title", default: "Daily Check-in", max: 100 },
  { field: "check_in_button_text", label: "Check-in button", default: "Check In", max: 50 },
  { field: "checked_in_button_text", label: "Checked-in button", default: "Checked In", max: 50 },
  { field: "check_in_ad_text", label: "Under the button", default: "Watch Ad", max: 50 },
  { field: "breakdown_title", label: "Breakdown title", default: "Your Event Points Breakdown", max: 100 },
  { field: "activities_title", label: "Activities title", default: "Eligible Point Activities", max: 100 },
  { field: "sponsor_title", label: "Sponsors title", default: "Sponsored By", max: 100 },
  { field: "sponsor_btn_text", label: "Sponsor button", default: "Become a Sponsor", max: 50 },
  { field: "rules_title", label: "Terms title", default: "Terms & Conditions", max: 100 },
];

export const RULES_MAX = 30;
export const RULE_MAX_LENGTH = 1000;

const THEME_KEYS = [
  "card_bg_type",
  "card_bg_image",
  ...COLOR_FIELDS.map((f) => f.field),
  ...TEXT_FIELDS.map((f) => f.field),
];

/** A blank theme: every key present, every value "use the default". */
export const emptyTheme = () => {
  const theme = Object.fromEntries(THEME_KEYS.map((key) => [key, ""]));
  theme.card_bg_type = "color";
  return theme;
};

/**
 * The API's theme -> form state.
 *
 * With no mode saved, an event that has an image is in image mode — the same
 * rule the app applies.
 */
export const themeFromApi = (apiTheme) => {
  const theme = emptyTheme();
  if (!apiTheme || typeof apiTheme !== "object") return theme;

  for (const key of THEME_KEYS) {
    if (apiTheme[key] != null) theme[key] = String(apiTheme[key]);
  }
  theme.card_bg_type = CARD_BG_TYPES.includes(apiTheme.card_bg_type)
    ? apiTheme.card_bg_type
    : apiTheme.card_bg_image
    ? "image"
    : "color";
  return theme;
};

/**
 * Form state -> the request's `theme`. Every key is sent, blanks as "", so a
 * field cleared in the form is cleared on the server too.
 *
 * `card_bg_image` goes back as loaded: the API recognises its own URL and keeps
 * the uploaded file rather than storing the absolute link.
 */
export const themePayload = (theme) =>
  Object.fromEntries(
    THEME_KEYS.map((key) => {
      const value = String(theme?.[key] ?? "").trim();
      return [key, COLOR_FIELDS.some((f) => f.field === key) ? value.toUpperCase() : value];
    })
  );

/** Rules from the API -> editable rows. */
export const rulesFromApi = (rules) =>
  (Array.isArray(rules) ? rules : []).map((text, i) => ({ _localId: `loaded-${i}`, text: String(text ?? "") }));

/** Rows -> the request's `rules`: blanks dropped, order kept. */
export const rulesPayload = (rows) =>
  (rows || []).map((row) => String(row.text ?? "").trim()).filter(Boolean);

/**
 * What stops this theme from being saved, or null.
 *
 * Anything the API would refuse blocks a save even while the design is off.
 * A missing image only does while it is on: a design being prepared may pick
 * image mode before the image is ready.
 *
 * @param {object} theme
 * @param {{ hasImage?: boolean, enabled?: boolean }} [options]
 *   hasImage  whether an image is uploaded or picked
 *   enabled   whether the design is switched on for the app
 */
export const themeProblem = (theme, { hasImage = false, enabled = true } = {}) => {
  const bad = COLOR_FIELDS.find(({ field }) => {
    const value = String(theme?.[field] ?? "").trim();
    return value && !isHexColor(value);
  });
  if (bad) return `${bad.label} must be a hex color like #636AE8 (or #636AE8B3 with transparency)`;

  if (enabled && theme?.card_bg_type === "image" && !hasImage) {
    return "Image mode needs a card image — upload one, paste a URL, or switch to Color";
  }

  const url = String(theme?.card_bg_image ?? "").trim();
  if (url && !/^https?:\/\//i.test(url)) {
    return "The card image URL must start with https://";
  }

  const long = TEXT_FIELDS.find(({ field, max }) => String(theme?.[field] ?? "").trim().length > max);
  if (long) return `${long.label} is too long — keep it under ${long.max} characters`;

  return null;
};

// ── colour helpers ─────────────────────────────────────────────────────────

/** `#RRGGBB` part, for an `<input type="color">`, which knows nothing of alpha. */
export const rgbOf = (hex) => String(hex || "#000000").slice(0, 7).toUpperCase();

/**
 * `#RRGGBB` / `#RRGGBBAA` -> `0xAARRGGBB`: what the API sends the app for this
 * colour (Flutter's `Color(int)` order — alpha first). Same rule as
 * `toFlutterColor` in the API; shown under each field so the admin and the
 * Flutter side can compare exact values.
 */
export const toFlutterColor = (hex) => {
  const value = String(hex ?? "").trim().toUpperCase();
  if (!isHexColor(value)) return null;
  return `0x${value.length === 9 ? value.slice(7, 9) : "FF"}${value.slice(1, 7)}`;
};

/**
 * A pasted Flutter colour `0xAARRGGBB` -> the form's `#RRGGBB(AA)`, or null if
 * it is not one. Fully opaque drops the alpha, as the opacity slider does.
 */
export const fromFlutterColor = (value) => {
  const match = /^0x([0-9a-f]{2})([0-9a-f]{6})$/i.exec(String(value ?? "").trim());
  if (!match) return null;
  const [, alpha, rgb] = match;
  return `#${rgb.toUpperCase()}${alpha.toUpperCase() === "FF" ? "" : alpha.toUpperCase()}`;
};

/**
 * Every colour and text key as the app will receive it: what was typed (if
 * valid), else the default. Same rule as `resolveTheme` in the API, including
 * the defaults that follow another key — the card and the check-in text follow
 * the primary colour, the gradient end and shadow follow the gradient start,
 * and a lone solid colour makes a one-colour card.
 */
export const resolveTheme = (theme = {}, eventName = "") => {
  const valid = (field) => (isHexColor(theme?.[field]) ? String(theme[field]).trim().toUpperCase() : "");
  const fixed = (field) => valid(field) || COLOR_FIELDS.find((f) => f.field === field).default;

  const primary = fixed("primary_color");
  const bg = valid("card_bg_color") || primary;
  const start = valid("card_gradient_start") || bg;
  const solid = valid("card_bg_color") && !valid("card_gradient_start") && !valid("card_gradient_end");

  const resolved = Object.fromEntries(COLOR_FIELDS.map(({ field }) => [field, fixed(field)]));
  Object.assign(resolved, {
    card_bg_color: bg,
    card_gradient_start: start,
    card_gradient_end: valid("card_gradient_end") || (solid ? bg : `${rgbOf(start)}B3`),
    card_shadow_color: valid("card_shadow_color") || `${rgbOf(start)}4D`,
    check_in_btn_text_color: valid("check_in_btn_text_color") || primary,
  });

  for (const { field, default: fallback } of TEXT_FIELDS) {
    resolved[field] = String(theme?.[field] ?? "").trim() || fallback || "";
  }
  resolved.card_title = String(theme?.card_title ?? "").trim() || String(eventName || "");

  return resolved;
};

/** The colour actually in effect for one field — see resolveTheme. */
export const effectiveColor = (theme, field) => resolveTheme(theme)[field];

// ── Terms & Conditions defaults ───────────────────────────────────────────

/**
 * The standard five rules: what a new event's Terms & Conditions start as, and
 * what the app is sent when none are written. The same wording as
 * `STANDARD_RULES` in the API.
 */
export const defaultRules = () => [
  "This event is completely free to enter and participate. No purchase or payment is required to join or win.",
  "Users compete by collecting Event Points through daily activities to rank up on the leaderboard.",
  "The event runs for the specified duration. Final rankings are determined when the event ends.",
  "Points can come from Daily Check-in, Daily Mining, Referral activity, Streak Bonus, and eligible Creator Hub activity.",
  "The top ranking positions on the leaderboard receive the published event prizes.",
];

/** The standard rules as editable rows, for a new event's form. */
export const defaultRuleRows = () => defaultRules().map((text, i) => ({ _localId: `default-${i}`, text }));

/** Opacity 0–100 from the alpha byte; 100 when there is none. */
export const opacityOf = (hex) => {
  const value = String(hex || "");
  if (value.length !== 9) return 100;
  return Math.round((parseInt(value.slice(7, 9), 16) / 255) * 100);
};

/** `#RRGGBB` + opacity -> `#RRGGBB` or `#RRGGBBAA` (fully opaque drops the alpha). */
export const withOpacity = (rgb, opacity) => {
  const pct = Math.max(0, Math.min(100, Math.round(Number(opacity))));
  if (pct >= 100) return rgbOf(rgb);
  const alpha = Math.round((pct / 100) * 255).toString(16).padStart(2, "0").toUpperCase();
  return `${rgbOf(rgb)}${alpha}`;
};

// ── card image upload ──────────────────────────────────────────────────────

/** Mirrors `readCardImageUpload` in the API. No SVG or GIF: the app cannot draw them. */
export const CARD_IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const CARD_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
export const CARD_IMAGE_ACCEPT = CARD_IMAGE_TYPES.join(",");
export const CARD_IMAGE_HINT = "PNG, JPG or WebP up to 5MB · wide images work best (about 2:1)";

export const cardImageFileError = (file) => {
  if (!file) return null;
  if (!CARD_IMAGE_TYPES.includes(String(file.type).toLowerCase())) {
    return `The card image must be a PNG, JPEG or WebP — got ${file.type || "no type"}`;
  }
  if (file.size > CARD_IMAGE_MAX_BYTES) {
    return `That image is ${(file.size / (1024 * 1024)).toFixed(1)}MB — the limit is 5MB`;
  }
  return null;
};
